<template>
  <!-- prettier-ignore -->
  <div class="screen">
    <Form.Form
      class="form"
      name="changeAuthInfo"
      tracking-disabled
      :validator="$v"
      @cancel="cancel"
      @submit="submit">

      <template v-if="!isVerificationNeeded">
        <Modal.Content>
          <div class="frames__frame">
            <the-typography
              type="heading04"
              v-text="$t('screen.onboarding.title')"
            />

            <the-typography
              type="bodyMediumLong"
              v-text="$t('screen.onboarding.existingCustomerText')"
            />

            <Form.PhoneInput
              v-model="formData.mobile"
              autofocus
              class="form__input"
              :delay="10000"
              name="mobile"
              required
              theme="light"
              :placeholder="$t('person.mobile')"
              :title="$t('person.mobile')"
              :validate="$v.mobile"
            />

            <Form.Input
              v-model="formData.email"
              class="form__input"
              required
              name="email"
              theme="light"
              type="email"
              :placeholder="$t('person.email')"
              :title="$t('person.email')"
              :validate="$v.email"
            />
          </div>
        </Modal.Content>
      </template>

      <template v-else>
        <Modal.Content>
          <div class="frames__frame">
            <the-typography
              type="heading04"
              v-text="$t('screen.onboarding.title')"
            />

            <the-typography
              type="bodyMediumLong"
              v-text="$t('screen.reentry.tanHelptext', {
                mobile: formData.mobile
              })"
            />

            <Form.CodeInput
              v-model="formData.code"
              numeric
              name="code"
              required
              theme="light"
              :title="$t('screen.reentry.insertTan')"
            />
          </div>
        </Modal.Content>
      </template>

      <Modal.Footer class="frames__footer">
        <template #secondary>
          <div class="frames__actions">
            <span> 
              <Form.Button
                v-if="isVerificationNeeded"
                link
                :disabled="isSmsDisabled"
                :text="$t('screen.reentry.resend')"
                @click="submit(false)"
              />
            </span>
            <Form.Button
              link
              :disabled="isVerificationNeeded"
              :text="$t('form.cancel')"
              @click="cancel"
            />
          </div>
        </template>
        <template #primary>
          <Form.Button
            primary
            type="submit"
            :text="$t('form.saveAndContinue')"
          />
        </template>
      </Modal.Footer>

    </Form.Form>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import useVuelidate from '@vuelidate/core'

import { events$, loading$ } from '@/services'

import { useBasket, useBrowser, useSession } from '@/hooks'

import * as Modal from '@/components/Modal'
import * as Form from '@/components/Form'

import { validations } from '@/validations/onboarding'
import { EVENT_BASKET, SOURCE } from '@/config/events'

// HOOKS
const { basket, persistBasket, updateOvpBasket } = useBasket()
const { browser } = useBrowser()
const { setSession } = useSession()

// INIT
const emit = defineEmits(['close'])

// DATA
const formData = reactive({
  code: undefined,
  email: basket.email,
  mobile: basket.mobile,
})
const isSmsDisabled = ref(false)
const isVerificationNeeded = ref(false)
const $v = useVuelidate(validations, formData, { $lazy: true })

// METHODS
function cancel() {
  emit('close')
}

async function submit(allowToken) {
  loading$.start()

  isSmsDisabled.value = true
  window.setTimeout(() => (isSmsDisabled.value = false), 60 * 2 * 1000)

  const params = {
    isDesktop: browser.isDesktop,
    emailType: 'OFFER_REENTRY',
  }

  if (allowToken && isVerificationNeeded.value) {
    params.mobileNumberVerificationSmsTan = formData.code
    delete formData.code
  }
  let __basket = formData

  try {
    __basket = await updateOvpBasket(__basket, {}, params)
    events$.emit(EVENT_BASKET.SAVED, {
      basket: __basket,
      source: SOURCE.ONBOARDING,
      update: false,
    })

    cancel()
  } catch (error) {
    if (error.infoMessages?.find(m => m.errorCode === 'MOBILE_NUMBER_VERIFICATION_REQUIRED')) {
      setSession(__basket)
      persistBasket(__basket)
      isVerificationNeeded.value = true
    }
  }

  loading$.end()
}
</script>

<style name="mobile" scoped>
.screen {
  .frames {
    display: flex;
    flex-direction: column;
    gap: 26px;

    .form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .frames__frame {
    background-color: var(--c-primary-neutral-2);
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .frames__footer :deep(.actions__secondary) {
    flex: 1;
  }

  .frames__actions {
    display: flex;
    flex: 1;
    justify-content: space-between;
    gap: 16px;

    div:last-child {
      display: flex;
      gap: 16px;
      align-self: flex-end;
    }
  }
}

.policy__title {
  font-weight: bold;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .screen {
    .frames__actions {
      display: flex;
      justify-content: space-between;
      gap: 16px;

      div:last-child {
        display: flex;
        gap: 16px;
        align-self: flex-end;
      }
    }
  }
}
</style>
